import React from 'react';
import { BrandsAPI } from '../../../../../../constructs/brands/interface';
import { useAPIContext } from '../../../../context/api';
import { useInitializeBanner } from '../use-initialize-banner';
import { useUploadMedia } from '../../../media/use-upload-media';
import { useClientId } from '../../../use-client-id';

export interface CreateHtmlBannerInput {
  title: string;
  media: File;
  width: number;
  height: number;
  clickUrl: string;
  exitId: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

export const useCreateHtmlBanner = () => {
  const { callEndpoint } = useAPIContext();
  const { initializeBanner } = useInitializeBanner();
  const { uploadVideoDirect, uploadPosterDirect } = useUploadMedia();
  const clientId = useClientId();
  const [loading, setLoading] = React.useState(false);

  const createHtmlBanner = React.useCallback(
    async (payload: CreateHtmlBannerInput) => {
      try {
        setLoading(true);

        const bannerId = await initializeBanner();

        const isVideo = payload.media.type.startsWith('video/');

        let uploadResult: { publicUrl: string } | null = null;

        if (isVideo) {
          uploadResult = await uploadVideoDirect('video', '.mp4', payload.media, clientId, bannerId, 'video');

          if (!uploadResult?.publicUrl) {
            throw new Error('Failed to upload video');
          }
        } else {
          const extMatch = payload.media.name.match(/\.[^.]+$/);
          const fileExtension = (extMatch ? extMatch[0] : '').toLowerCase();

          uploadResult = await uploadPosterDirect('image', fileExtension, payload.media, bannerId, clientId);

          if (!uploadResult?.publicUrl) {
            throw new Error('Failed to upload poster image');
          }
        }

        const bannerRes = await callEndpoint<BrandsAPI, 'create-html-banner'>('brands', '/banners/html5', 'POST', {
          bannerId,
          title: payload.title,
          mediaData: {
            width: payload.width,
            height: payload.height,
            clickUrl: payload.clickUrl,
            exitId: payload.exitId,
            utm_source: payload.utm_source,
            utm_medium: payload.utm_medium,
            utm_campaign: payload.utm_campaign,
            utm_term: payload.utm_term,
            utm_content: payload.utm_content,
            media: isVideo
              ? { kind: 'video', value: { originalUrl: uploadResult.publicUrl, status: 'ready' } }
              : { kind: 'image', value: uploadResult.publicUrl }
          }
        });

        if (!bannerRes) {
          throw new Error('Failed to create banner');
        }

        return {
          banner: bannerRes.banner
        };
      } catch (error) {
        console.error('[useCreateHtmlBanner] Error:', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [callEndpoint, initializeBanner, uploadVideoDirect, uploadPosterDirect, clientId]
  );

  return {
    createHtmlBanner,
    loading
  };
};
